import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';

const PAGE_SIZE = 50;

function FeedbackItem({ feedback }) {
  const [expanded, setExpanded] = useState(false);
  const MAX_LENGTH = 250;
  const text = feedback.comment || '';
  const isLong = text.length > MAX_LENGTH;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ color: i < rating ? '#f59e0b' : '#d1d5db', fontSize: '16px' }}>★</span>
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ minWidth: '90px', color: '#6b7280', fontSize: '14px' }}>
        {formatDate(feedback.created_at)}
      </div>
      <div style={{ minWidth: '90px' }}>
        {feedback.rating ? renderStars(feedback.rating) : '—'}
      </div>
      <div style={{ flex: 1, fontSize: '14px', color: '#374151' }}>
        {isLong && !expanded ? (
          <>
            {text.slice(0, MAX_LENGTH)}...{' '}
            <span
              onClick={() => setExpanded(true)}
              style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
            >
              Показать полностью
            </span>
          </>
        ) : (
          <>
            {text}
            {isLong && expanded && (
              <>
                {' '}
                <span
                  onClick={() => setExpanded(false)}
                  style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
                >
                  Свернуть
                </span>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sort: 'created_at'
  });

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const totalPages = Math.ceil(totalUsers / PAGE_SIZE);

  const buildUsersParams = useCallback((page, searchTerm, statusFilter, sortFilter) => {
    const params = {
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      sort: sortFilter || 'created_at',
    };
    if (searchTerm) {
      params.search = searchTerm;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    return params;
  }, []);

  const fetchUsers = useCallback(async (
    page = currentPage,
    searchTerm = filters.search,
    statusFilter = filters.status,
    sortFilter = filters.sort,
  ) => {
    try {
      setLoading(true);
      const data = await api.getUsers(buildUsersParams(page, searchTerm, statusFilter, sortFilter));
      setUsers(data.items || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      toast.error('Ошибка загрузки пользователей');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.search, filters.status, filters.sort, buildUsersParams]);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchUsers(currentPage, filters.search, filters.status, filters.sort);
  }, [currentPage, filters.search, filters.status, filters.sort, fetchUsers]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchUsers(currentPage, filters.search, filters.status, filters.sort);
    }, 60 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentPage, filters.search, filters.status, filters.sort, fetchUsers]);

  const fetchClasses = async () => {
    try {
      const data = await api.getClasses();
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const getClassName = (classId) => {
    if (!classId) return 'Не определён';
    if (classes.length === 0) return 'Загрузка...';
    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : 'Не определён';
  };

  const getStatusMeta = (status) => {
    const normalized = (status || '').toLowerCase();
    if (!normalized) return { label: 'Не указан', className: 'status-inactive' };
    if (normalized === 'block') return { label: 'Заблокирован', className: 'status-cancelled' };
    if (normalized === 'inactive') return { label: 'Неактивен', className: 'status-pending' };
    if (normalized === 'new') return { label: 'Новый', className: 'status-in-progress' };
    if (normalized === 'active') return { label: 'Активен', className: 'status-completed' };
    return { label: status, className: 'status-inactive' };
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(1, filters.search, filters.status, filters.sort);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleUserClick = async (userId) => {
    try {
      const userData = await api.getUser(userId);
      setSelectedUser(userData);
      setShowModal(true);
    } catch (error) {
      toast.error('Ошибка загрузки данных пользователя');
      console.error('Error fetching user details:', error);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя? Это действие необратимо.')) {
      try {
        await api.deleteUser(userId);
        toast.success('Пользователь удален');
        fetchUsers(currentPage, filters.search, filters.status, filters.sort);
      } catch (error) {
        toast.error(error.message || 'Ошибка при удалении пользователя');
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    if (key === 'status' || key === 'sort') {
      setCurrentPage(1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Никогда';
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 7;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    return (
      <div className="pagination">
        <button
          className="btn btn-outline btn-small"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
        <button
          className="btn btn-outline btn-small"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lsaquo;
        </button>

        {startPage > 1 && (
          <>
            <button className="btn btn-outline btn-small" onClick={() => handlePageChange(1)}>1</button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}

        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
          <button
            key={page}
            className={`btn btn-small ${page === currentPage ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button className="btn btn-outline btn-small" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
          </>
        )}

        <button
          className="btn btn-outline btn-small"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rsaquo;
        </button>
        <button
          className="btn btn-outline btn-small"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Пользователи {totalUsers > 0 && <span style={{ fontSize: '0.7em', color: '#6b7280', fontWeight: 'normal' }}>({totalUsers})</span>}</h2>
        <div className="action-buttons">
          <button
            onClick={() => fetchUsers(currentPage, filters.search, filters.status, filters.sort)}
            className="btn btn-outline"
            disabled={loading}
          >
            Обновить
          </button>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filters-header">
          <h3 className="filters-title">Фильтры</h3>
        </div>
        <div className="filters-content">
          <div className="filter-group">
            <label className="filter-label">Поиск</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className="filter-input filter-input-search"
                placeholder="Поиск по имени или username..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <button className="btn btn-outline btn-small" onClick={handleSearch}>
                Найти
              </button>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">Статус</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">Все статусы</option>
              <option value="new">Новые</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
              <option value="blocked">Заблокированные</option>
              <option value="unspecified">Не указан</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Сортировка</label>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className="filter-select"
            >
              <option value="created_at">По дате регистрации</option>
              <option value="last_activity">По активности</option>
              <option value="name">По имени</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="table-container">
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Загрузка пользователей...</p>
          </div>
        </div>
      ) : (
        <div className="table-container">
          {users.length === 0 ? (
            <div className="empty-state">
              <p>Пользователи не найдены</p>
            </div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Имя</th>
                    <th>Интерес</th>
                    <th>Статус</th>
                    <th>Заявки</th>
                    <th>Последняя активность</th>
                    <th>Дата регистрации</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="clickable text-sm font-mono" onClick={() => handleUserClick(user.id)}>{user.id}</td>
                      <td className="clickable" onClick={() => handleUserClick(user.id)}>
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            {user.username && (
                              <div className="text-sm text-gray-500">
                                @{user.username}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="clickable text-sm text-gray-900" onClick={() => handleUserClick(user.id)}>
                        {getClassName(user.class_id)}
                      </td>
                      <td className="clickable" onClick={() => handleUserClick(user.id)}>
                        <span className={`status-badge ${getStatusMeta(user.status).className}`}>
                          {getStatusMeta(user.status).label}
                        </span>
                      </td>
                      <td className="clickable text-sm text-gray-900" onClick={() => handleUserClick(user.id)}>
                        {user.applications_count || 0}
                      </td>
                      <td className="clickable text-sm text-gray-500" onClick={() => handleUserClick(user.id)}>
                        {formatDate(user.last_activity)}
                      </td>
                      <td className="clickable text-sm text-gray-500" onClick={() => handleUserClick(user.id)}>
                        {formatDate(user.created_at)}
                      </td>
                      <td>
                        <button onClick={() => handleDelete(user.id)} className="btn btn-danger btn-small">Удалить</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Показано {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalUsers)} из {totalUsers}
                </div>
                {renderPagination()}
              </div>
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Детали пользователя"
        size="large"
      >
        {selectedUser && (
          <div className="user-details">
            <div className="user-info-section">
              <h4>Основная информация</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="detail-row">
                    <span className="label">ID:</span>
                    <span>{selectedUser.id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Имя:</span>
                    <span>{selectedUser.first_name} {selectedUser.last_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Username:</span>
                    <span>{selectedUser.username || 'Не указан'}</span>
                  </div>
                </div>
                <div>
                  <div className="detail-row">
                    <span className="label">Telegram ID:</span>
                    <span>{selectedUser.telegram_id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Статус:</span>
                    <span className={
                      (selectedUser.status || '').toLowerCase() === 'block'
                        ? 'text-red-600'
                        : !(selectedUser.status || '').trim()
                          ? 'text-gray-500'
                          : 'text-green-600'
                    }>
                      {getStatusMeta(selectedUser.status).label}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Последняя активность:</span>
                    <span>{formatDate(selectedUser.last_activity)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Интерес:</span>
                    <span>{getClassName(selectedUser.class_id)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Источник:</span>
                    <span>
                      {selectedUser.lead_source_title
                        ? `${selectedUser.lead_source_title}${selectedUser.lead_source_chosen_at ? ` (${formatDate(selectedUser.lead_source_chosen_at)})` : ''}`
                        : 'Не указан'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {selectedUser.feedbacks && selectedUser.feedbacks.length > 0 && (
              <div className="user-info-section">
                <h4>Отзывы ({selectedUser.feedbacks.length})</h4>
                <div className="feedbacks-list">
                  {selectedUser.feedbacks.map((feedback) => (
                    <FeedbackItem key={feedback.id} feedback={feedback} />
                  ))}
                </div>
              </div>
            )}

            {selectedUser.applications && selectedUser.applications.length > 0 && (
              <div className="user-info-section">
                <h4>Заявки ({selectedUser.applications.length})</h4>
                <div className="applications-list">
                  {selectedUser.applications.map((app) => (
                    <div key={app.id} className="application-item">
                      <span>#{app.id} - {app.theme}</span>
                      <span className={`status-badge status-${app.status}`}>
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedUser.notes && (
              <div className="user-info-section">
                <h4>Заметки</h4>
                <p>{selectedUser.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Users;
